/**
 * Representatives API Client
 * GAP-2: Meghatalmazott kezelés — partner-szintű képviselők
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export interface Representative {
  id: string;
  tenantId: string;
  partnerId: string;
  name: string;
  phone: string | null;
  email: string | null;
  idNumber: string | null;
  validFrom: string;
  validUntil: string | null;
  isActive: boolean;
  canRent: boolean;
  canReturn: boolean;
  canSign: boolean;
  canPayCash: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRepresentativeInput {
  name: string;
  phone?: string | undefined;
  email?: string | undefined;
  idNumber?: string | undefined;
  validFrom: string;
  validUntil?: string | undefined;
  isActive?: boolean | undefined;
  canRent?: boolean | undefined;
  canReturn?: boolean | undefined;
  canSign?: boolean | undefined;
  canPayCash?: boolean | undefined;
  notes?: string | undefined;
}

export type UpdateRepresentativeInput = Partial<CreateRepresentativeInput>;

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get all representatives for a partner
 */
export async function getRepresentatives(partnerId: string): Promise<Representative[]> {
  const response = await api.get<{ data: Representative[] }>(
    `/partners-direct/${partnerId}/representatives`
  );
  return response.data;
}

/**
 * Get only active representatives for a partner
 */
export async function getActiveRepresentatives(partnerId: string): Promise<Representative[]> {
  const response = await api.get<{ data: Representative[] }>(
    `/partners-direct/${partnerId}/representatives/active`
  );
  return response.data;
}

/**
 * Create a new representative for a partner
 */
export async function createRepresentative(
  partnerId: string,
  data: CreateRepresentativeInput
): Promise<Representative> {
  const response = await api.post<{ data: Representative }>(
    `/partners-direct/${partnerId}/representatives`,
    data
  );
  return response.data;
}

/**
 * Update a representative
 */
export async function updateRepresentative(
  partnerId: string,
  repId: string,
  data: UpdateRepresentativeInput
): Promise<Representative> {
  const response = await api.patch<{ data: Representative }>(
    `/partners-direct/${partnerId}/representatives/${repId}`,
    data
  );
  return response.data;
}

/**
 * Delete a representative
 */
export async function deleteRepresentative(partnerId: string, repId: string): Promise<void> {
  await api.delete<{ data: { success: true } }>(
    `/partners-direct/${partnerId}/representatives/${repId}`
  );
}

/**
 * Activate a representative
 */
export async function activateRepresentative(
  partnerId: string,
  repId: string
): Promise<Representative> {
  const response = await api.patch<{ data: Representative }>(
    `/partners-direct/${partnerId}/representatives/${repId}/activate`,
    {}
  );
  return response.data;
}

/**
 * Deactivate a representative
 */
export async function deactivateRepresentative(
  partnerId: string,
  repId: string
): Promise<Representative> {
  const response = await api.patch<{ data: Representative }>(
    `/partners-direct/${partnerId}/representatives/${repId}/deactivate`,
    {}
  );
  return response.data;
}
